import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { DatabaseStar, Folder } from 'iconoir-react';

import { ResourceCard, ResourceCardLogo } from 'components/blocks/resource-card';
import { configureCardGridClassName, configureCardSizeClassName } from 'constant/configure-layout';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { useConfig } from 'hooks/use-config';
import { useGetFolderList } from 'features/chat/hooks/use-kb';
import type { IFolder } from 'features/chat/agent-chat-module/types/kb.service.type';
import { cn } from 'lib/utils';
import { AddFolderModal } from './components/add-folder-modal';
import { FolderCardDropdown } from './components/folder-card-dropdown';
import { VisibilityBadge } from 'components/blocks/visibility-field';
import { useTranslation } from 'react-i18next';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import IntegrationsPage from 'pages/integrations/integrations-page';

function FolderListSkeleton() {
  return (
    <div className={configureCardGridClassName}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Card
          key={index}
          className="h-[240px] min-h-[240px] overflow-hidden rounded-2xl border border-grey-100 bg-white shadow-none dark:border-border dark:bg-card"
        >
          <div className="flex h-full flex-col gap-3 p-5">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <div className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-muted" />
              <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-muted" />
            </div>
            <div className="h-4 w-32 animate-pulse rounded bg-muted" />
            <div className="h-3 w-full animate-pulse rounded bg-muted" />
            <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
            <div className="mt-auto flex items-center justify-between gap-2 pt-4">
              <div className="h-6 w-28 shrink-0 animate-pulse rounded-full bg-muted" />
              <div className="h-5 w-24 animate-pulse rounded-md bg-muted" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function FolderCard({
  folder,
  projectKey,
  onOpen,
}: {
  folder: IFolder;
  projectKey: string;
  onOpen: (folderId: string) => void;
}) {
  const { t } = useTranslation();
  const name = folder.Name || t('UNTITLED_FOLDER') || 'Untitled folder';
  const description =
    folder.Description || t('NO_DESCRIPTION_PROVIDED') || 'No description provided.';
  const embeddingModel =
    folder.EmbeddingModelConfiguration?.Model || t('NO_EMBEDDING_MODEL') || 'No embedding model';

  return (
    <ResourceCard
      title={name}
      description={description}
      className={configureCardSizeClassName}
      icon={
        <ResourceCardLogo name={name}>
          <Folder width={20} height={20} strokeWidth={1.75} />
        </ResourceCardLogo>
      }
      actions={<FolderCardDropdown projectKey={projectKey} folder={folder} />}
      onSelect={() => onOpen(folder._id)}
      footer={
        <div className="flex min-h-7 items-center justify-between gap-2">
          <span className="flex min-w-0 flex-1 items-center gap-1.5" title={embeddingModel}>
          
            
          </span>
          <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1.5">
            <VisibilityBadge
              value={folder.Visibility ?? folder.visibility}
              className="shrink-0"
              uppercase={false}
            />
          </div>
        </div>
      }
    />
  );
}

export default function KnowledgeBasePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [integrationsSearch, setIntegrationsSearch] = useState('');
  const activeTab = searchParams.get('tab') === 'integrations' ? 'integrations' : 'folders';
  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || '';
  const { data, isLoading, isFetching } = useGetFolderList(projectKey, !!projectKey);
  const folders = data?.data?.folders || [];

  const filteredFolders = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return folders;
    }

    return folders.filter((folder) => {
      const haystack = [
        folder.Name,
        folder.Description,
        folder.EmbeddingModelConfiguration?.Model,
        folder.EmbeddingModelConfiguration?.Provider,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [folders, search]);

  const isLoadingFolders = isLoading || isFetching;

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'integrations') {
      next.set('tab', 'integrations');
    } else {
      next.delete('tab');
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <ConfigurationLayout>
      <div className="flex w-full min-w-0 flex-col gap-6 pb-6">
        <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <ConfigurationHeader
            title={t('KNOWLEDGE_BASE') || 'Knowledge Base'}
            subtitle={
              t('KNOWLEDGE_BASE_DESCRIPTION') ||
              'Browse and manage the folders used to ground your agents and AI workflows.'
            }
            icon={
              <DatabaseStar
                width={20}
                height={20}
                strokeWidth={1.75}
                className="text-grey-900 dark:text-foreground"
              />
            }
          />

          {activeTab === 'folders' && <AddFolderModal projectKey={projectKey} />}
        </div>

        <div className="inline-flex flex-wrap items-center gap-2" role="tablist">
          {[
            { label: t('FOLDERS') || 'Folders', value: 'folders' as const },
            { label: t('INTEGRATIONS_KEY') || 'Integrations', value: 'integrations' as const },
          ].map((tab) => {
            const isActive = activeTab === tab.value;

            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleTabChange(tab.value)}
                className={cn(
                  'inline-flex h-10 items-center rounded-lg border px-3 text-sm font-normal leading-[1.2] transition-colors',
                  isActive
                    ? 'border-grey-100 bg-grey-50 text-grey-900 dark:border-true-white/40 dark:bg-true-white/15 dark:text-true-white'
                    : 'border-transparent text-grey-500 hover:bg-grey-50 hover:text-grey-900 dark:text-true-white/75 dark:hover:bg-true-white/10 dark:hover:text-true-white'
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 sm:max-w-[460px] dark:border-border dark:bg-card">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center p-[3px]">
            <Search className="h-[15px] w-[15px] text-grey-400" />
          </div>
          {activeTab === 'folders' ? (
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('SEARCH_FOLDERS') || 'Search folders...'}
              className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-[1.2] shadow-none placeholder:text-grey-400 focus-visible:ring-0 dark:bg-transparent"
            />
          ) : (
            <Input
              value={integrationsSearch}
              onChange={(event) => setIntegrationsSearch(event.target.value)}
              placeholder={t('SEARCH_INTEGRATIONS') || 'Search integrations'}
              className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-[1.2] shadow-none placeholder:text-grey-400 focus-visible:ring-0 dark:bg-transparent"
            />
          )}
        </div>
      </div>

      {activeTab === 'integrations' ? (
        <IntegrationsPage
          embedded
          search={integrationsSearch}
          onSearchChange={setIntegrationsSearch}
        />
      ) : (
      <div className="flex min-w-0 flex-col gap-3">
        {/* <div className="flex min-w-0 items-center justify-between gap-3 sm:gap-4">
          <div className="flex min-w-0 items-center gap-2">
            <Folder width={20} height={20} strokeWidth={1.75} className="text-grey-900 dark:text-foreground" />
            <h2 className="min-w-0 text-base font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
              {t('ALL_FOLDERS') || 'All folders'}
            </h2>
          </div>
          <span className="inline-flex shrink-0 items-center rounded-lg bg-grey-50 p-3 text-sm font-normal leading-[1.2] text-grey-900 dark:bg-white/10 dark:text-foreground">
            {filteredFolders.length} {folderCountLabel}
          </span>
        </div> */}

        {isLoadingFolders ? (
          <FolderListSkeleton />
        ) : filteredFolders.length > 0 ? (
          <div className={configureCardGridClassName}>
            {filteredFolders.map((folder) => (
              <FolderCard
                key={folder._id}
                folder={folder}
                projectKey={projectKey}
                onOpen={(folderId) => navigate(`/knowledge-base/${folderId}`)}
              />
            ))}
          </div>
        ) : folders.length > 0 ? (
          <Card className="rounded-lg border border-dashed border-grey-100 bg-white shadow-none dark:border-border dark:bg-card">
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              {t('NO_FOLDERS_MATCHED_SEARCH') || 'No folders matched your search.'}
            </div>
          </Card>
        ) : (
          <Card className="flex h-[315px] w-full flex-col items-center justify-center gap-6 rounded-lg border border-grey-100 bg-white p-6 shadow-none dark:border-border dark:bg-card">
            <div className="flex flex-col items-center gap-1.5 text-center">
              <Folder width={20} height={20} strokeWidth={1.75} className="text-grey-500 dark:text-muted-foreground" />
              <p className="text-base font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                {t('NO_FOLDERS_YET') || 'No folders yet'}
              </p>
              <p className="max-w-lg text-sm font-normal leading-[1.6] text-grey-500 dark:text-muted-foreground">
                {t('NO_FOLDERS_DESCRIPTION') ||
                  'Create your first folder to start organizing documents, notes, links, and Q&A content.'}
              </p>
            </div>
            <AddFolderModal
              projectKey={projectKey}
              triggerLabel={t('CREATE_FOLDER') || 'Create folder'}
            />
          </Card>
        )}
      </div>
      )}
    </ConfigurationLayout>
  );
}
